import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import FinanceRecoveryPanels from "./shared/FinanceRecoveryPanels";
import {
  RadioGroup,
  WarningBanner,
  useConfirm,
} from "./shared/components";
import {
  getNoDuesFormDetails,
  getNoDuesSettlementDetail,
  settleNoDuesItem,
  closeNoDuesSettlement,
  downloadNoDuesSettlementReport,
  formatDate,
  NoDuesSettlementDetail,
  NoDuesSettlementItem,
} from "./services/eSeparation.service";

// Per-item editing buffer keyed by item id.
interface ItemDraft {
  collected: boolean;
  collected_amount: string;
  remarks: string;
}

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return formatDate(v);
  }
  return String(v);
};

const NoDuesSettlementAction: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const state = (location.state as any) || {};
  const requestId: string =
    state.request_id || (params.id ? decodeURIComponent(params.id) : "");

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<NoDuesSettlementDetail | null>(null);
  const [drafts, setDrafts] = useState<Record<number, ItemDraft>>({});
  const [savingItem, setSavingItem] = useState<number | null>(null);
  const [closeComments, setCloseComments] = useState("");
  const [closing, setClosing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openingForm, setOpeningForm] = useState(false);

  const load = useCallback(async (silent: boolean = false) => {
    if (!requestId) return;
    if (!silent) setLoading(true);
    try {
      const res = await getNoDuesSettlementDetail(requestId);
      const data = res.data ?? null;
      setDetail(data);
      const next: Record<number, ItemDraft> = {};
      (data?.items ?? []).forEach((i) => {
        next[i.id] = {
          collected: !!i.collected,
          collected_amount: i.collected_amount ? String(i.collected_amount) : "",
          remarks: i.remarks || "",
        };
      });
      setDrafts(next);
    } catch {
      if (!silent) toast.error("Failed to load settlement detail");
    }
    if (!silent) setLoading(false);
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  // Recovery heads, managers and the nodal all work on the same request, so keep the
  // screen live (the nodal sees every submission / verification as it happens).
  const hasFlow = !!detail?.finance?.has_flow;
  const flowClosed = String(detail?.settlement?.status || "").toUpperCase() === "CLOSED";
  useEffect(() => {
    if (!hasFlow || flowClosed) return;
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [hasFlow, flowClosed, load]);

  // Opens the No-Dues form exactly as the employee / HR filed it, with each
  // department's items shown inside it.
  const openNoDuesForm = () =>
    navigate(routes.Esepration.noDuesFormView.replace(":id", encodeURIComponent(requestId)), {
      state: { request_id: requestId },
    });

  const settlement = detail?.settlement ?? null;
  const items = detail?.items ?? [];
  const req = detail?.request ?? null;
  const closed = String(settlement?.status || "").toUpperCase() === "CLOSED";
  const pendingCount = items.filter((i) => !i.acted_at).length;
  // A zero-dues settlement (nothing to recover, sent purely for
  // Establishment's own record/sign-off) has no items at all — that's
  // trivially "all acted", not blocked.
  const allActed = pendingCount === 0;

  const patchDraft = (id: number, patch: Partial<ItemDraft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSettleItem = async (item: NoDuesSettlementItem) => {
    const draft = drafts[item.id];
    if (!draft) return;
    setSavingItem(item.id);
    const res = await settleNoDuesItem({
      item_id: item.id,
      collected: draft.collected,
      collected_amount: draft.collected
        ? Number(draft.collected_amount) || Number(item.amount_due) || 0
        : 0,
      remarks: draft.remarks.trim(),
    });
    setSavingItem(null);
    if (res.success) {
      toast.success(draft.collected ? "Marked collected" : "Marked not collected");
      load();
    } else {
      toast.error(res.message || "Failed to settle item");
    }
  };

  const handleClose = async () => {
    if (!allActed) {
      toast.error(`Settle all items before closing (${pendingCount} pending)`);
      return;
    }
    const ok = await confirm(
      "Closing finalizes the No-Dues request. Continue?",
      { title: "Close Settlement", confirmLabel: "Close", tone: "danger" }
    );
    if (!ok) return;
    setClosing(true);
    const res = await closeNoDuesSettlement({
      request_id: requestId,
      comments: closeComments.trim(),
    });
    setClosing(false);
    if (res.success) {
      toast.success("Settlement closed");
      load();
    } else {
      toast.error(res.message || "Failed to close settlement");
    }
  };

  // Opens the employee's original resignation form (read-only, as the employee
  // filed it). The parent id comes with the settlement detail; if the backend
  // did not supply it, fall back to the No-Dues enablement record, whose
  // request_id is the parent resignation / case.
  const openResignationForm = async () => {
    setOpeningForm(true);
    let parentId: string | null | undefined = detail?.request?.orig_request_id;
    if (!parentId) {
      const nd = await getNoDuesFormDetails(requestId);
      parentId = nd.data?.request_id || null;
    }
    setOpeningForm(false);
    if (!parentId || parentId === requestId) {
      toast.error("Could not find the original resignation for this request");
      return;
    }
    navigate(routes.Esepration.approvalDetail.replace(":id", parentId), {
      state: { request_id: parentId, type: "Intimation", role: "Employee", viewOnly: true },
    });
  };

  const handleDownload = async () => {
    setDownloading(true);
    const res = await downloadNoDuesSettlementReport(requestId);
    setDownloading(false);
    if (res.success) toast.success("Settlement report downloaded");
    else toast.error(res.message || "Failed to download report");
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading settlement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!settlement) {
    return (
      <div className="page-wrapper">
        <div className="content pb-5">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold text-dark mb-0">Finance Settlement</h4>
              <div className="text-muted small">{requestId}</div>
            </div>
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={openingForm}
              onClick={openResignationForm}
            >
              <i className="ti ti-file-text me-1" />
              {openingForm ? "Opening…" : "View Resignation Form"}
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body p-4">
              <WarningBanner
                type="info"
                message="No settlement found for this request."
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.noDuesSettlementInbox);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    Finance Settlement
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Settlement Action
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">Finance Settlement</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={openingForm}
              onClick={openResignationForm}
            >
              <i className="ti ti-file-text me-1" />
              {openingForm ? "Opening…" : "View Resignation Form"}
            </button>
            <button className="btn btn-outline-primary btn-sm" onClick={openNoDuesForm}>
              <i className="ti ti-clipboard-list me-1" />
              View No-Dues Form
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Highlighted at the top deliberately — Establishment needs to see
            this before anything else, since it's a self-reported declaration
            that only Establishment can actually verify/act on. */}
        {req?.deputation_availed === "Y" && req?.deputation_remittance_received === "Y" && (
          <WarningBanner
            type="success"
            message="The employee has declared that all remittances from deputation service have been received in DMRC. Please verify before closing this settlement."
          />
        )}

        {/* Sub-header Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: "#FDE8E0",
                    color: "#993C1D",
                  }}
                >
                  <i className="ti ti-cash" style={{ fontSize: 22 }} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#344054",
                      lineHeight: "1.3",
                    }}
                  >
                    {req?.emp_name || "Finance Settlement"}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#667085",
                      lineHeight: "1.4",
                      marginTop: "2px",
                    }}
                  >
                    {requestId}
                    {req?.emp_no ? ` • ${req.emp_no}` : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 01: SETTLEMENT SUMMARY */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  01
                </span>
                Settlement Summary
              </h5>
            </div>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Status</label>
                <div className="pt-1">
                  <span
                    className={`badge ${
                      closed ? "bg-success" : "bg-warning text-dark"
                    }`}
                    style={{ fontSize: 13 }}
                  >
                    {closed ? "Closed" : "In Progress"}
                  </span>
                </div>
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Total Due</label>
                <input
                  type="text"
                  className="form-control bg-light fw-bold text-danger"
                  value={`₹ ${Number(settlement.total_due).toLocaleString("en-IN")}`}
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Total Collected</label>
                <input
                  type="text"
                  className="form-control bg-light fw-bold text-success"
                  value={`₹ ${Number(settlement.total_collected).toLocaleString("en-IN")}`}
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Items</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={`${items.length - pendingCount} of ${items.length} settled`}
                  readOnly
                />
              </div>

              {settlement.dispatched_at && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Received On</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDate(settlement.dispatched_at)}
                    readOnly
                  />
                </div>
              )}

              {closed && settlement.closed_at && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Closed On</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDate(settlement.closed_at)}
                    readOnly
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 02: ALL DEPARTMENTS' NO-DUES — every department's outcome, the
            cleared ones included, so Establishment sees the whole picture and
            not just the dues. */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                02
              </span>
              All Departments&apos; No-Dues
            </h5>
            <div className="text-muted small mt-1 ms-4 ps-2">
              What every clearing department reported — including those that cleared with nothing due.
            </div>
          </div>
          <div className="card-body p-4">
            {!(detail?.departments && detail.departments.length) ? (
              <WarningBanner type="info" message="No department clearances are recorded for this request." />
            ) : (
              <div className="d-flex flex-column gap-3">
                {detail.departments.map((d) => {
                  const st = String(d.status).toUpperCase();
                  const color = st === "CLEARED" ? "#049123" : st === "REJECTED" ? "#FF0F0F" : "#BA7517";
                  const label = st === "CLEARED" ? "Cleared" : st === "REJECTED" ? "Dues Found" : "Pending";
                  const total = d.items.reduce((sum, i) => sum + (Number(i.amount_due) || 0), 0);
                  return (
                    <div
                      key={d.id}
                      className="p-3 rounded-3 border bg-light"
                      style={{ borderLeft: `4px solid ${color}` }}
                    >
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <div className="fw-bold text-dark">{d.department_name}</div>
                          <small className="text-muted">
                            {d.approver_name || d.approver_emp_id || "—"}
                            {d.approver_emp_id ? ` (${d.approver_emp_id})` : ""}
                            {d.acted_at ? ` · ${fmt(d.acted_at)}` : ""}
                          </small>
                        </div>
                        <span
                          className="px-3 py-1 rounded-pill fw-medium"
                          style={{ fontSize: 12, color, background: `${color}18` }}
                        >
                          {label}
                        </span>
                      </div>
                      {d.remarks && (
                        <div className="small mt-2 text-secondary bg-white p-2 rounded border fst-italic">
                          &quot;{d.remarks}&quot;
                        </div>
                      )}
                      {d.items.length > 0 ? (
                        <div className="table-responsive mt-2">
                          <table className="table table-sm align-middle mb-0 bg-white">
                            <thead>
                              <tr>
                                <th>Item</th>
                                <th>Comments</th>
                                <th className="text-end" style={{ width: 130 }}>Amount Due</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.items.map((it, k) => (
                                <tr key={k}>
                                  <td>{it.item_name}</td>
                                  <td className="text-muted small">{it.comments || "—"}</td>
                                  <td className="text-end">₹ {Number(it.amount_due).toFixed(2)}</td>
                                </tr>
                              ))}
                              <tr>
                                <td colSpan={2} className="fw-semibold text-end">Department total</td>
                                <td className="text-end fw-semibold">₹ {total.toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="small text-muted mt-2">
                          {st === "CLEARED" ? "No dues reported — cleared." : "No checklist items recorded."}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {hasFlow && detail?.finance && (
          <>
            <FinanceRecoveryPanels
              requestId={requestId}
              finance={detail.finance}
              deptDues={items}
              departments={detail.departments || []}
              closed={closed}
              confirm={confirm}
              onChanged={() => load(true)}
            />
            {closed && (
              <div className="mb-4">
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? <span className="spinner-border spinner-border-sm" /> : <i className="ti ti-download" />}
                  <span>Download Report PDF</span>
                </button>
              </div>
            )}
          </>
        )}

        {/* Legacy settlements (created before the recovery-head flow) keep the
            item-by-item settle + close screen. */}
        {!hasFlow && (
          <>
        {/* SECTION 03: DEPARTMENT-WISE RECOVERY */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  03
                </span>
                Department-wise Recovery
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Settle each department-reported due one by one.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {items.length === 0 ? (
              <WarningBanner
                type="info"
                message="No recovery items in this settlement."
              />
            ) : (
              items.map((item, i) => {
                const draft =
                  drafts[item.id] || {
                    collected: false,
                    collected_amount: "",
                    remarks: "",
                  };
                const acted = !!item.acted_at;
                const locked = closed || acted;
                return (
                  <div
                    key={item.id}
                    className="p-3 mb-3 border rounded-3 bg-light"
                    style={{ borderColor: "#e9ecef" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                          Item {i + 1}
                        </span>
                        <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">
                          {item.source_department_name}
                        </span>
                      </div>
                      {acted ? (
                        <span
                          className="badge d-inline-flex align-items-center gap-1"
                          style={{
                            background: item.collected ? "#E4F7E8" : "#FDE5E5",
                            color: item.collected ? "#049123" : "#C0282C",
                            fontSize: 12,
                          }}
                        >
                          <i
                            className={`ti ${
                              item.collected ? "ti-circle-check" : "ti-circle-x"
                            }`}
                          />
                          {item.collected ? "Collected" : "Not Collected"}
                        </span>
                      ) : (
                        <span
                          className="badge d-inline-flex align-items-center gap-1"
                          style={{ background: "#FC9D4520", color: "#BA7517", fontSize: 12 }}
                        >
                          <i className="ti ti-clock" />
                          Pending
                        </span>
                      )}
                    </div>

                    <div className="row g-3">
                      <div className="col-lg-6 col-md-6 col-sm-12">
                        <label className="form-label fw-semibold text-secondary small mb-1">Item Name</label>
                        <input
                          type="text"
                          className="form-control bg-white fw-medium text-dark"
                          value={item.item_name}
                          readOnly
                        />
                      </div>

                      <div className="col-lg-6 col-md-6 col-sm-12">
                        <label className="form-label fw-semibold text-secondary small mb-1">Amount Due</label>
                        <input
                          type="text"
                          className="form-control bg-white fw-bold text-danger"
                          value={`₹ ${Number(item.amount_due).toLocaleString("en-IN")}`}
                          readOnly
                        />
                      </div>

                      {locked ? (
                        <>
                          {acted && item.collected && (
                            <div className="col-lg-6 col-md-6 col-sm-12">
                              <label className="form-label fw-semibold text-secondary small mb-1">Collected</label>
                              <input
                                type="text"
                                className="form-control bg-white fw-bold text-success"
                                value={`₹ ${Number(item.collected_amount).toLocaleString("en-IN")}`}
                                readOnly
                              />
                            </div>
                          )}
                          {item.remarks && (
                            <div className="col-12">
                              <label className="form-label fw-semibold text-secondary small mb-1">Remarks</label>
                              <div className="p-2 px-3 rounded-2 bg-white text-dark small border">
                                {item.remarks}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="col-12 mt-3 pt-3 border-top">
                          <div className="mb-3">
                            <label className="form-label fw-semibold text-secondary small mb-2">
                              Collected?
                            </label>
                            <RadioGroup
                              name={`settle-collected-${item.id}`}
                              value={draft.collected ? "yes" : "no"}
                              onChange={(v) =>
                                patchDraft(item.id, { collected: v === "yes" })
                              }
                              options={[
                                { label: "Collected", value: "yes" },
                                { label: "Not Collected", value: "no" },
                              ]}
                            />
                          </div>

                          {draft.collected && (
                            <div className="mb-3">
                              <label className="form-label fw-semibold text-secondary small mb-1">
                                Collected Amount (₹)
                              </label>
                              <input
                                type="number"
                                className="form-control bg-white"
                                placeholder={String(item.amount_due)}
                                value={draft.collected_amount}
                                onChange={(e) =>
                                  patchDraft(item.id, {
                                    collected_amount: e.target.value,
                                  })
                                }
                              />
                            </div>
                          )}

                          <div className="mb-3">
                            <label className="form-label fw-semibold text-secondary small mb-1">
                              Remarks
                            </label>
                            <textarea
                              className="form-control bg-white"
                              rows={2}
                              placeholder="Any notes for this item..."
                              value={draft.remarks}
                              onChange={(e) =>
                                patchDraft(item.id, { remarks: e.target.value })
                              }
                            />
                          </div>

                          <button
                            type="button"
                            className="btn text-white d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                            style={{
                              background: draft.collected ? "#049123" : "#C0282C",
                              borderRadius: 10,
                              height: 42,
                            }}
                            onClick={() => handleSettleItem(item)}
                            disabled={savingItem === item.id}
                          >
                            {savingItem === item.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <i className="ti ti-circle-check" />
                            )}
                            <span>{draft.collected ? "Mark Collected" : "Mark Not Collected"}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* SECTION 03: FINALIZE */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  04
                </span>
                Finalize
              </h5>
            </div>
          </div>
          <div className="card-body p-4">
            {closed ? (
              <WarningBanner
                type="success"
                message={
                  settlement.comments
                    ? `Settlement closed. ${settlement.comments}`
                    : "Settlement closed. The No-Dues request is finalized."
                }
              />
            ) : (
              <>
                {!allActed && (
                  <WarningBanner
                    type="warning"
                    message={`Settle all ${items.length} item(s) before closing (${pendingCount} pending).`}
                  />
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Closing Comments
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Final remarks for this settlement..."
                    value={closeComments}
                    onChange={(e) => setCloseComments(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                  style={{
                    height: 44,
                    borderRadius: 10,
                    opacity: !allActed || closing ? 0.5 : 1,
                  }}
                  onClick={handleClose}
                  disabled={!allActed || closing}
                >
                  {closing ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                  <span>Comment &amp; Close</span>
                </button>
              </>
            )}

            <div className="mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                style={{ borderRadius: 10 }}
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <i className="ti ti-download" />
                )}
                <span>Download Report PDF</span>
              </button>
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NoDuesSettlementAction;
